import { GitHubIcon } from '@/components/oauth-icons';

// SPDX-License-Identifier: BUSL-1.1

/**
 * Sign-in page — supports both API key auth and Better Auth session auth.
 *
 * Tab 1: API Key — for CLI agents and programmatic access
 * Tab 2: Email / Password — for human users via Better Auth
 * OAuth buttons: GitHub, Google (redirect to Better Auth endpoints)
 *
 */

import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { fetchAndStoreBearerJwt, useAuth } from '@/lib/auth';
import { API_BASE, AUTH_BASE } from '@/lib/api-client';
import { showError } from '@/lib/toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

// ─── API Key form ────────────────────────────────────────────────────────────

const apiKeySchema = z.object({
  apiKey: z.string().min(1, 'API key is required'),
});

type ApiKeyFormValues = z.infer<typeof apiKeySchema>;

// ─── Better Auth email/password form ─────────────────────────────────────────

const emailPasswordSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});

type EmailPasswordFormValues = z.infer<typeof emailPasswordSchema>;

// ─── OAuth icons (inline SVG — no extra deps) ────────────────────────────────

// ─── Health check ────────────────────────────────────────────────────────────

type HealthState = {
  loading: boolean;
  setupRequired: boolean | null;
  apiReachable: boolean;
};

function useHealthCheck() {
  const [health, setHealth] = useState<HealthState>({
    loading: true,
    setupRequired: null,
    apiReachable: true,
  });

  useEffect(() => {
    let cancelled = false;

    async function check() {
      try {
        const res = await fetch(`${API_BASE}/health`);
        if (!cancelled) {
          if (!res.ok) {
            // /health returns 503 only when the DB is truly down. A non-2xx
            // means the API cannot serve requests — surface it as unreachable.
            setHealth({ loading: false, setupRequired: null, apiReachable: false });
            return;
          }
          const data: { setup_required?: boolean } = await res.json();
          setHealth({
            loading: false,
            setupRequired: data.setup_required ?? null,
            apiReachable: true,
          });
        }
      } catch {
        if (!cancelled) {
          setHealth({ loading: false, setupRequired: null, apiReachable: false });
        }
      }
    }

    check();
    return () => {
      cancelled = true;
    };
  }, []);

  return health;
}

// ─── Sign In page ────────────────────────────────────────────────────────────

export function SignInPage() {
  const { login, loginWithSession } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'apikey' | 'email'>('email');
  const [serverError, setServerError] = useState('');
  const [apikeyError, setApikeyError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const health = useHealthCheck();

  // If returnTo is set, the archive's edge function sent the user here with
  // `staging.build.ceo/login?returnTo=https://archive-staging.build.ceo/e/:id/:id`.
  // Honour it after login so the user lands on the archive URL they originally
  // requested. Accept both absolute URLs (cross-origin from archive) and relative
  // paths. Validate absolute URLs against known archive origins for security.
  //
  // OAuth error: Better Auth redirects back here with ?error=invalid_code on
  // GitHub token exchange failure (e.g., code already used or redirect_uri mismatch).
  // Show a toast so the user understands what happened instead of a silent blank page.
  const returnTo = (() => {
    try {
      const raw = new URLSearchParams(window.location.search).get('returnTo') ?? '';
      if (!raw) return '/';

      // Accept absolute URLs from archive origins (cross-origin redirect)
      if (raw.startsWith('https://') || raw.startsWith('http://')) {
        try {
          const url = new URL(raw);
          // Only allow redirects to known archive origins
          const allowedHosts = [
            'archive.build.ceo',
            'archive-staging.build.ceo',
            'localhost', // dev
          ];
          if (allowedHosts.some((h) => url.hostname === h || url.hostname.endsWith(`.${h}`))) {
            return raw;
          }
        } catch {
          // Invalid URL, fall through
        }
        return '/';
      }

      // Accept relative paths
      if (raw.startsWith('/')) return raw;
      return '/';
    } catch {
      return '/';
    }
  })();

  // Display OAuth error from Better Auth callback redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthError = params.get('error');
    if (oauthError) {
      // Clean the URL without reloading so the error doesn't persist on refresh
      const cleanUrl = window.location.origin + window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);
      const errorMessages: Record<string, string> = {
        invalid_code:
          'GitHub sign-in failed: authorization code expired or already used. Please try again.',
        access_denied: 'GitHub sign-in was cancelled.',
        please_restart_the_process: 'OAuth session expired. Please start sign-in again.',
        state_mismatch: 'OAuth security check failed. Please try again.',
        state_not_found: 'OAuth session not found. Please try again.',
      };
      const message = errorMessages[oauthError] ?? `OAuth error: ${oauthError}. Please try again.`;
      showError('GitHub sign-in failed', message);
    }
  }, []);

  const apiKeyForm = useForm<ApiKeyFormValues>({
    resolver: zodResolver(apiKeySchema),
    defaultValues: { apiKey: '' },
  });

  const emailForm = useForm<EmailPasswordFormValues>({
    resolver: zodResolver(emailPasswordSchema),
    defaultValues: { email: '', password: '' },
  });

  // If already authenticated, redirect to home (or returnTo if set)
  const { isAuthenticated, isLoading } = useAuth();
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate(returnTo, { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, returnTo]);

  async function handleApiKeySubmit(values: ApiKeyFormValues) {
    setApikeyError('');
    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/configs`, {
        headers: { Authorization: `Bearer ${values.apiKey.trim()}` },
      });
      if (res.ok) {
        login(values.apiKey.trim());
        navigate(returnTo);
      } else {
        const msg = 'Invalid API key. Please try again.';
        setApikeyError(msg);
        showError(msg);
      }
    } catch {
      const msg = 'Could not reach the server. Check your connection.';
      setApikeyError(msg);
      showError(msg);
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleEmailPasswordSubmit(values: EmailPasswordFormValues) {
    setServerError('');
    setIsSubmitting(true);
    try {
      const res = await fetch(`${AUTH_BASE}/api/auth/sign-in/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: values.email,
          password: values.password,
        }),
      });

      if (res.ok) {
        // Fetch the session to get user info and update React state
        const sessionRes = await fetch(`${AUTH_BASE}/api/auth/get-session`, {
          credentials: 'include',
        });
        if (sessionRes.ok) {
          // Better Auth returns `null` (HTTP 200) when there is no session.
          const { user } = (await sessionRes.json()) ?? {};
          if (user) {
            // Mint a Bearer JWT from build-ceo-auth — required because build.ceo and
            // build-ceo-auth live on different origins, so the build-ceo-auth cookie can't
            // be sent on cross-site requests to the build.ceo API.
            await fetchAndStoreBearerJwt();
            loginWithSession(user);
          }
        }
        navigate(returnTo);
      } else {
        const data = await res.json().catch(() => ({}));
        setServerError(data.error ?? 'Invalid email or password.');
        showError('Sign-in failed', data.error ?? 'Invalid email or password.');
      }
    } catch {
      setServerError('Could not reach the server. Check your connection.');
      showError('Network error', 'Could not reach the server.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-950">
      <div className="w-full max-w-sm rounded-lg border border-neutral-800 bg-neutral-900 p-8">
        {/* Logo */}
        <div className="mb-8 font-mono text-xl font-bold tracking-tight text-neutral-100">
          build.ceo<span className="animate-pulse text-emerald-400">_</span>
        </div>

        {/* API unreachable banner */}
        {!health.loading && !health.apiReachable && (
          <div className="mb-4 rounded border border-red-900/50 bg-red-950/30 px-4 py-3">
            <p className="text-sm font-medium text-red-400">
              Cannot reach the build.ceo API. Is the server running?
            </p>
            <p className="mt-1 font-mono text-xs text-red-400/70">Try: docker compose up -d</p>
          </div>
        )}

        {/* Tabs */}
        <div className="mb-6 flex rounded-lg border border-neutral-700 bg-neutral-800 p-1">
          <button
            type="button"
            onClick={() => setActiveTab('email')}
            className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              activeTab === 'email'
                ? 'bg-neutral-700 text-white'
                : 'text-neutral-400 hover:text-neutral-200'
            }`}
          >
            Email
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('apikey')}
            className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              activeTab === 'apikey'
                ? 'bg-neutral-700 text-white'
                : 'text-neutral-400 hover:text-neutral-200'
            }`}
          >
            API Key
          </button>
        </div>

        {/* ── Email / Password tab ── */}
        {activeTab === 'email' && (
          <>
            <Form {...emailForm}>
              <form
                onSubmit={emailForm.handleSubmit(handleEmailPasswordSubmit)}
                className="flex flex-col gap-4"
              >
                <FormField
                  control={emailForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-neutral-400">Email</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          autoComplete="email"
                          placeholder="ada@example.com"
                          className="border-neutral-700 bg-neutral-800 text-neutral-100 placeholder-neutral-600 focus-visible:border-emerald-500"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={emailForm.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-neutral-400">Password</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          autoComplete="current-password"
                          placeholder="••••••••"
                          className="border-neutral-700 bg-neutral-800 text-neutral-100 placeholder-neutral-600 focus-visible:border-emerald-500"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {serverError && (
                  <p className="rounded border border-red-900/50 bg-red-950/30 px-3 py-2 text-sm text-red-400">
                    {serverError}
                  </p>
                )}

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="mt-2 bg-emerald-600 text-white hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isSubmitting ? 'Signing in...' : 'Sign in'}
                </Button>
              </form>
            </Form>

            {/* OAuth buttons */}
            <div className="mt-4 flex flex-col gap-2">
              <div className="relative flex items-center py-2">
                <div className="flex-1 border-t border-neutral-700" />
                <span className="mx-3 text-xs text-neutral-500">or continue with</span>
                <div className="flex-1 border-t border-neutral-700" />
              </div>
              <Button
                variant="outline"
                type="button"
                onClick={async () => {
                  // Better Auth socialProviders.github: POST /sign-in/social with provider.
                  // Response: 302 redirect to GitHub OAuth authorization page.
                  const callbackURL = `${window.location.origin}/`;
                  const res = await fetch(`${AUTH_BASE}/api/auth/sign-in/social`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ provider: 'github', callbackURL }),
                    credentials: 'include',
                  });
                  if (res.ok) {
                    const data = await res.json();
                    if (data.url) {
                      window.location.href = data.url;
                    }
                  } else {
                    const err = await res.json().catch(() => ({ error: 'OAuth sign-in failed' }));
                    showError('GitHub sign-in failed', err.error ?? 'Please try again.');
                  }
                }}
                className="flex items-center justify-center gap-2 border-neutral-700 bg-neutral-800 text-neutral-200 hover:bg-neutral-700 hover:text-white"
              >
                <GitHubIcon className="h-4 w-4" />
                GitHub
              </Button>
              {/*
                Google OAuth disabled — build.ceo only supports GitHub sign-in.
                To enable: add socialProviders.google to createAuth() in auth.ts
                and update this button to POST /api/auth/sign-in/social.
              */}
            </div>

            <p className="mt-4 text-center text-sm text-neutral-500">
              No account?{' '}
              <Link to="/sign-up" className="text-emerald-400 hover:text-emerald-300">
                Create one
              </Link>
            </p>
          </>
        )}

        {/* ── API Key tab ── */}
        {activeTab === 'apikey' && (
          <>
            {!health.loading && health.apiReachable && health.setupRequired === true && (
              <div className="mb-4 rounded border border-blue-800/50 bg-blue-950/30 px-4 py-3">
                <p className="text-sm font-medium text-blue-300">
                  Welcome to build.ceo! No API keys exist yet.
                </p>
                <p className="mt-1 text-xs text-blue-300/70">
                  Run{' '}
                  <code className="rounded bg-blue-900/40 px-1 py-0.5 font-mono">
                    ./scripts/setup.sh
                  </code>{' '}
                  to create your first API key, or see the Quick Start guide.
                </p>
              </div>
            )}

            <Form {...apiKeyForm}>
              <form
                onSubmit={apiKeyForm.handleSubmit(handleApiKeySubmit)}
                className="flex flex-col gap-4"
              >
                {/* Hidden username field for accessibility */}
                <input
                  type="text"
                  name="username"
                  autoComplete="username"
                  value="api-key"
                  readOnly
                  className="hidden"
                  aria-hidden="true"
                  tabIndex={-1}
                />
                <FormField
                  control={apiKeyForm.control}
                  name="apiKey"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-neutral-400">API Key</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          autoComplete="current-password"
                          placeholder="ceo_sk_..."
                          className="border-neutral-700 bg-neutral-800 text-neutral-100 placeholder-neutral-600 focus-visible:border-emerald-500"
                          {...field}
                        />
                      </FormControl>
                      {!health.loading && health.apiReachable && health.setupRequired === false && (
                        <FormDescription className="text-neutral-500">
                          Enter your API key (starts with ceo_sk_). Need a key? Ask your admin or
                          run: <code className="font-mono">ceo keys create</code>
                        </FormDescription>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {apikeyError && (
                  <p className="rounded border border-red-900/50 bg-red-950/30 px-3 py-2 text-sm text-red-400">
                    {apikeyError}
                  </p>
                )}

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="mt-2 bg-emerald-600 text-white hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isSubmitting ? 'Verifying...' : 'Sign in'}
                </Button>
              </form>
            </Form>
          </>
        )}
      </div>
    </div>
  );
}
